import { List as ImmutableList } from 'immutable';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import { accountsValueChangesSelectors } from '../accounts-value-changes';

import { ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY } from './accountsBalancesHistoryTypes';

export const getStore = (state) => state[ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY];

export const getLoadPending = (state) => getStore(state).loadPending;
export const getIsLoading = (state) => getStore(state).isLoading;
export const getError = (state) => getStore(state).error;
export const getRows = (state) => getStore(state).rows;
export const getColumns = (state) => getStore(state).columns;
export const getStartDate = (state) => getStore(state).startOn;
export const getEndDate = (state) => getStore(state).endOn;
export const getTimeStep = (state) => getStore(state).timestep;
export const getResourcesById = (state) => getStore(state).rows;

const { getDailyValueChanges } = accountsValueChangesSelectors;

export const addDateFieldstoCellData = createSelector(getRows, getColumns, (balanceRows, balanceColumns) => {
  if (!balanceRows || !balanceColumns || balanceRows.length === 0 || balanceColumns.length === 0) {
    return [];
  }
  const orderedBalanceRows = balanceRows.map((row) => {
    const newRow = { ...row };
    newRow.cellData = newRow.cellData || [];
    balanceColumns.forEach((column) => {
      const existingCell = newRow.cellData.find((cell) => cell.columnId === column.id);
      if (!existingCell) {
        const newCell = {
          id: column.id,
          columnId: column.id,
          value: null,
          date: column.field || null,
          fieldName: column.headerName || null,
        };
        newRow.cellData.push(newCell);
      } else {
        existingCell.date = column.field || null;
        existingCell.fieldName = column.headerName || null;
      }
    });
    newRow.cellData.sort((a, b) => a.columnId - b.columnId);
    return newRow;
  });
  return orderedBalanceRows;
});

export const getBalancesHistoryTotals = createSelector(
  addDateFieldstoCellData,
  (modifiedRows) => modifiedRows && modifiedRows?.find((row) => row.name === 'Total'),
);

export const getSortedBalancesHistoryByAccount = createSelector(
  (state, accounts) => accounts,
  addDateFieldstoCellData,
  (accounts, modifiedData) => {
    const accountsBalances = modifiedData?.filter((account) => accounts?.includes(account.entityId));
    accountsBalances?.forEach((accountbalance, index) => {
      accountbalance?.cellData?.sort(
        (balance) => DateTime.fromISO(balance.date),
        (valueA, valueB) => valueA.toMillis() - valueB.toMillis(),
      );
      accountbalance.colorIndex = index % 11;
    });
    return accountsBalances;
  },
);

export const getDateRange = createSelector(getColumns, (columnData) => {
  const dateRange = [];
  columnData?.forEach((column) => column.field && dateRange.push(column.field));
  return dateRange;
});

export const formatBalancesForChart = createSelector(
  getDateRange,
  (state, props) => getSortedBalancesHistoryByAccount(state, props.accounts),
  (selectedDateRange, accountData) => {
    let chartData = [];
    chartData = selectedDateRange?.map((myDate) => {
      const result = { date: myDate, containsBalances: false };
      accountData?.forEach((account) => {
        if (account.cellData) {
          // Find the current balance for the given date
          const currentBalance = account.cellData.find((balance) => balance.date === myDate).value;
          if (currentBalance !== null) {
            result[`${account.entityId}-balance`] = currentBalance;
            result.containsBalances = true;
          }
        }
      });
      return result;
    });
    return chartData;
  },
);

export const getZillowHomeValueHistory = createSelector(getBalancesHistoryTotals, (balanceTotal) =>
  balanceTotal?.cellData?.filter((item) => item.date),
);

export const getSortedBalancesForTable = createSelector(
  (state, props) => getSortedBalancesHistoryByAccount(state, props.accounts),
  getDailyValueChanges,
  (state, props) => props.startDate,
  (state, props) => props.endDate,
  (state, props) => props.orderBy,
  (state, props) => props.sortDirection,
  (sortedBalances, dailyValueChanges, startDate, endDate, orderBy, sortDirection) => {
    const formattedBalances = sortedBalances
      .map((account) => {
        if (!account) return;
        if (!account.cellData || account.cellData.length === 0) {
          return {
            colorIndex: account.colorIndex,
            accountName: account.name,
            accountId: account.entityId,
            oneDayBalance: null,
            oneDayPercent: null,
            specialDayBalance: null,
            specialDayPercent: null,
            todayBalance: null,
          };
        }
        const { colorIndex } = account;
        const accountName = account.name;

        const valueChange = dailyValueChanges?.find((item) => item?.entityId === account.entityId);

        const oneDayBalance = valueChange ? valueChange.cellData[0]?.value : null;
        const oneDayPercent = valueChange ? valueChange.cellData[1]?.value : null;
        const todayBalance = valueChange ? valueChange.cellData[2]?.value : null;

        const specialStartDay = account.cellData.filter((day) => day.date === startDate);
        const specialEndDay = account.cellData.filter((day) => day.date === endDate);
        const specialDayBalance =
          specialStartDay.length && specialEndDay.length ? specialEndDay[0]?.value - specialStartDay[0]?.value : null;
        const specialDayPercent =
          specialStartDay.length && specialEndDay.length
            ? ((specialEndDay[0]?.value - specialStartDay[0]?.value) / specialStartDay[0]?.value) * 100
            : null;

        return {
          colorIndex,
          accountId: account.entityId,
          accountName,
          oneDayBalance,
          oneDayPercent,
          specialDayBalance,
          specialDayPercent,
          todayBalance,
        };
      })
      .sort((unalteredValueA, unalteredValueB) => {
        if (orderBy === 'accountName') {
          const valueA = unalteredValueA[orderBy] ? String(unalteredValueA[orderBy]) : 'ZZZZZZZZZZZZZZ'; //  Undefined values go to the end of the list
          const valueB = unalteredValueB[orderBy] ? String(unalteredValueB[orderBy]) : 'ZZZZZZZZZZZZZZ';
          if (sortDirection === 'asc') {
            return valueA.localeCompare(valueB, undefined);
          }
          return valueB.localeCompare(valueA, undefined);
        }
        const valueA = unalteredValueA[orderBy] != null ? Number(unalteredValueA[orderBy]) : -99999999999999; //  Undefined values go to the end of the list
        const valueB = unalteredValueB[orderBy] != null ? Number(unalteredValueB[orderBy]) : -99999999999999;

        //  Using regular numeric comparisons because localeCompare numeric does NOT account for negative values!
        if (sortDirection === 'asc') {
          return valueB - valueA;
        }
        return valueA - valueB;
      });

    return ImmutableList(formattedBalances);
  },
);
